import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PlaidLinkButton from "../../components/common/PlaidLinkButton"; // Import the new component
import { Helmet } from "react-helmet-async";
import bankLinkingImage from "../../assets/images/bank-linking.jpeg";
import { UserBank } from "../../domain/types/banks/UserBank";
import { useUserBanks } from "../../domain/services/hooks/useUserBanks";
import BankNameLoader from "../../components/common/BankNameLoader";
import LoadingState from "../../components/common/LoadingState";
import ErrorState from "../../components/common/ErrorState";
import { useBankAccounts } from "../../domain/services/hooks/useBankAccounts";
import { BankAccount } from "../../domain/types/banks/BankAccount";

const ConnectBankAccounts: React.FC = () => {
  const {
    userBanks,
    loading: userBankLoading,
    error: userBankError,
  } = useUserBanks();
  const {
    bankAccounts,
    loading: bankAccountsLoading,
    error: bankAccountsError,
  } = useBankAccounts();
  const [localUserBanks, setLocalUserBanks] = useState<UserBank[]>(userBanks);
  const [localBankAccounts, setLocalBankAccounts] =
    useState<BankAccount[]>(bankAccounts);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (userBankLoading || bankAccountsLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }

    if (userBankError || bankAccountsError) {
      setError(userBankError || bankAccountsError);
    } else {
      setError(null);
    }

    if (!loading && userBanks) {
      setLocalUserBanks(userBanks);
    }

    if (!loading && bankAccounts) {
      setLocalBankAccounts(bankAccounts);
    }
  }, [
    userBanks,
    loading,
    userBankLoading,
    userBankError,
    bankAccountsLoading,
    bankAccountsError,
    bankAccounts,
  ]);

  // const handleAccountLinked = (userBankAccount: UserBank) => {
  //   setLocalUserBankAccounts((prevAccounts) => [
  //     ...prevAccounts,
  //     userBankAccount,
  //   ]);
  // };

  if (loading) {
    return <LoadingState message="Loading bank accounts..." />;
  }

  if (error) {
    return <ErrorState error={error} title="Error loading bank accounts" />;
  }

  return (
    <>
      <Helmet>
        <title>Connect Bank Accounts - OmniButler</title>
      </Helmet>
      <section className="relative table w-full py-36 bg-gray-50 dark:bg-slate-800">
        <div className="container relative">
          <div className="grid md:grid-cols-12 grid-cols-1 items-center gap-[30px]">
            <div className="md:col-span-full text-center">
              <h1 className="font-bold lg:leading-normal leading-normal text-4xl lg:text-5xl mb-5 text-black dark:text-white">
                Connect Your Bank Accounts
              </h1>
            </div>
            <div className="md:col-span-7">
              <div className="lg:me-6">
                <p className="text-slate-400 max-w-xl">
                  Securely connect your bank accounts to OmniButler to get a
                  unified view of your family&#39;s finances. We use Plaid, a
                  leading financial data aggregator, to ensure your data is safe
                  and protected.
                </p>

                <div className="mt-6">
                  <PlaidLinkButton />
                  {/*<PlaidLinkButton onAccountLinked={handleAccountLinked} />*/}
                </div>

                {localUserBanks.length > 0 && (
                  <>
                    <div className="mt-8">
                      <h6 className="font-semibold text-base mb-2 text-black dark:text-white">
                        Your Linked Bank Accounts
                      </h6>
                      <div className="space-y-4">
                        {localUserBanks.map((userBank) => (
                          <div
                            key={userBank.userBankId}
                            className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
                          >
                            <BankNameLoader
                              bank={
                                localBankAccounts.filter(
                                  (bankAccount) =>
                                    bankAccount.bankId == userBank.bankId,
                                )[0]
                              }
                            />
                            <span className="text-green-600 font-medium">
                              Connected
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-8">
                      <Link
                        to="/dashboard"
                        className="text-indigo-600 hover:text-indigo-700 font-semibold"
                      >
                        Go to Dashboard
                      </Link>
                    </div>
                  </>
                )}

                <div className="mt-8">
                  <h6 className="font-semibold text-base mb-2 text-black dark:text-white">
                    Your Security is Our Priority
                  </h6>
                  <ul className="text-slate-400 list-disc list-inside">
                    <li>
                      We use bank-level encryption to protect your financial
                      data.
                    </li>
                    <li>We never store your bank login credentials.</li>
                    <li>
                      Your data is only used to provide you with personalized
                      financial insights.
                    </li>
                  </ul>
                </div>

                <div className="mt-8">
                  <h6 className="font-semibold text-base mb-2 text-black dark:text-white">
                    How It Works
                  </h6>
                  <ul className="text-slate-400 list-disc list-inside">
                    <li>
                      Click &#34;Connect a Bank Account&#34; to securely link
                      your accounts through Plaid.
                    </li>
                    <li>
                      Select your bank and securely enter your login
                      credentials.
                    </li>
                    <li>
                      OmniButler will automatically sync your account balances
                      and transactions.
                    </li>
                  </ul>
                </div>

                <div className="mt-8">
                  <h6 className="font-semibold text-base mb-2 text-black dark:text-white">
                    Ready to get started?
                  </h6>
                  <p className="text-slate-400">
                    Connect your accounts now and experience the power of
                    OmniButler.
                  </p>
                </div>
              </div>
            </div>

            <div className="md:col-span-5">
              <img src={bankLinkingImage} alt="Securely link your account" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConnectBankAccounts;
